import React, { useState, useEffect, useCallback, useMemo } from 'react';
import AppSelector from './AppStore/AppSelector';
import TestFlightSection from './AppStore/TestFlightSection';
import VersionManager from './AppStore/VersionManager';
import LocalizationManager from './AppStore/LocalizationManager';
import ScreenshotManager from './AppStore/ScreenshotManager';
import AppInformation from './AppStore/AppInformation';
import CategoryManager from './AppStore/CategoryManager';
import AgeRating from './AppStore/AgeRating';
import ContentRights from './AppStore/ContentRights';
import ReviewInformation from './AppStore/ReviewInformation';
import PricingAvailability from './AppStore/PricingAvailability';
import MonetizationIAP from './AppStore/MonetizationIAP';
import MonetizationSubscriptions from './AppStore/MonetizationSubscriptions';
import SubmitButton from './AppStore/SubmitButton';
import { getApps, getAppDetails, getAppStoreVersions } from '../services/api';

function AppStoreManager({ onAppDataUpdate, activeSection, onSectionChange }) {
  const [selectedApp, setSelectedApp] = useState(null);
  const [appDetails, setAppDetails] = useState(null);
  const [currentVersion, setCurrentVersion] = useState(null);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState([]);

  const sections = useMemo(() => [
    { id: 'testflight', label: 'TestFlight', icon: '🚀' },
    { id: 'version', label: 'Version', icon: '📦' },
    { id: 'localization', label: 'Localization', icon: '🌍' },
    { id: 'screenshots', label: 'Screenshots', icon: '📸' },
    { id: 'information', label: 'Information', icon: 'ℹ️' },
    { id: 'categories', label: 'Categories', icon: '📂' },
    { id: 'content-rights', label: 'Content Rights', icon: '⚖️' },
    { id: 'age-rating', label: 'Age Rating', icon: '🎯' },
    { id: 'review', label: 'Review Info', icon: '📝' },
    { id: 'pricing', label: 'Pricing & Availability', icon: '💰' },
    { id: 'monetization-iap', label: 'In-App Purchases', icon: '🛒' },
    { id: 'monetization-subscriptions', label: 'Subscriptions', icon: '💳' }
  ], []);

  const loadAppData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // Load app details
      const detailsResponse = await getAppDetails(selectedApp.id);
      if (detailsResponse.data.success) {
        setAppDetails(detailsResponse.data);
      }

      // Load versions
      const versionsResponse = await getAppStoreVersions(selectedApp.id);
      if (versionsResponse.data.success && versionsResponse.data.versions.length > 0) {
        setVersions(versionsResponse.data.versions);
        // Get the latest version in preparation
        const prepVersion = versionsResponse.data.versions.find(
          v => v.attributes?.appStoreState === 'PREPARE_FOR_SUBMISSION'
        );
        setCurrentVersion(prepVersion || versionsResponse.data.versions[0]);
      }
    } catch (err) {
      const errorMsg = typeof err.response?.data?.error === 'object'
        ? err.response.data.error.message || 'Failed to load app data'
        : err.response?.data?.error || err.message || 'Failed to load app data';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [selectedApp]);

  useEffect(() => {
    if (selectedApp) {
      loadAppData();
    }
  }, [selectedApp, loadAppData]);

  useEffect(() => {
    // Update sidebar data
    onAppDataUpdate && onAppDataUpdate({
      appName: selectedApp?.attributes?.name,
      currentVersion,
      sections
    });
  }, [selectedApp, currentVersion, onAppDataUpdate, sections]);

  const validateSubmission = () => {
    const errors = [];
    
    if (!currentVersion) {
      errors.push('No version selected');
    }

    // Add more validation as needed
    
    setValidationErrors(errors);
    return errors.length === 0;
  };

  const isCurrentVersionEditable = () => {
    if (!currentVersion) return false;
    const state = currentVersion.attributes?.appStoreState;
    return ['PREPARE_FOR_SUBMISSION', 'READY_FOR_REVIEW', 'DEVELOPER_REJECTED', 'REJECTED'].includes(state);
  };

  const isAnyVersionEditable = () => {
    if (!versions || versions.length === 0) return false;
    return versions.some(version => 
      ['PREPARE_FOR_SUBMISSION', 'READY_FOR_REVIEW', 'DEVELOPER_REJECTED', 'REJECTED'].includes(version.attributes?.appStoreState)
    );
  };

  const getTabEligibility = (tabId) => {
    if (!currentVersion) return { eligible: false, reason: 'No version selected' };

    const versionState = currentVersion.attributes?.appStoreState;

    switch (tabId) {
      case 'testflight':
        // TestFlight can always be viewed, but linking/unlinking depends on version state
        return { eligible: true, canModify: isCurrentVersionEditable() };

      case 'version':
      case 'screenshots':
      case 'localization':
        // These can only be modified for versions in preparation
        return { 
          eligible: true, 
          canModify: isCurrentVersionEditable(),
          reason: isCurrentVersionEditable() ? null : `Version must be in "Prepare for Submission", "Ready for Review", "Developer Rejected", or "Rejected" state. Current state: ${versionState}`
        };

      case 'name':
      case 'keywords':
      case 'information':
        // App info can be modified when any version is in preparation
        const canModifyAppInfo = isAnyVersionEditable();
        return {
          eligible: true,
          canModify: canModifyAppInfo,
          reason: canModifyAppInfo ? null : 'App information can only be edited when you have a version in "Prepare for Submission", "Ready for Review", "Developer Rejected", or "Rejected" state'
        };

      case 'category':
      case 'rating':
      case 'content':
      case 'categories':
      case 'age-rating':
      case 'content-rights':
      case 'review':
        // These are app-level settings that can be modified when any version is in preparation
        const canModifyAppLevel = isAnyVersionEditable();
        return {
          eligible: true,
          canModify: canModifyAppLevel,
          reason: canModifyAppLevel ? null : 'App settings can only be modified when you have a version in "Prepare for Submission", "Ready for Review", "Developer Rejected", or "Rejected" state'
        };

      case 'pricing':
        // Pricing can be managed independently of version state
        return { eligible: true, canModify: true };

      case 'iap':
      case 'subs':
      case 'monetization-iap':
      case 'monetization-subscriptions':
        // IAP and subscriptions can be managed independently of version state
        return { eligible: true, canModify: true };

      default:
        return { eligible: true, canModify: true };
    }
  };

  const tabs = [
    { id: 'testflight', label: 'TestFlight & Builds', icon: '🚀', section: 1 },
    { id: 'version', label: 'Version Details', icon: '📝', section: 2 },
    { id: 'screenshots', label: 'Screenshots', icon: '📱', section: 2 },
    { id: 'localization', label: 'Description', icon: '📄', section: 2 },
    { id: 'information', label: 'App Information', icon: 'ℹ️', section: 3 },
    { id: 'categories', label: 'Category', icon: '📂', section: 4 },
    { id: 'age-rating', label: 'Age Rating', icon: '🎯', section: 4 },
    { id: 'content-rights', label: 'Content Rights', icon: '⚖️', section: 4 },
    { id: 'review', label: 'App Review', icon: '✅', section: 4 },
    // Section 5
    { id: 'pricing', label: 'Pricing & Availability', icon: '💰', section: 5 },
    { id: 'monetization-iap', label: 'In‑App Purchases', icon: '🛒', section: 5 },
    { id: 'monetization-subscriptions', label: 'Subscriptions', icon: '💳', section: 5 },
  ];

  if (!selectedApp) {
    return (
      <div className="content-main appstore-manager">
        <div className="section-header">
          <h1 className="section-title">🏪 App Store</h1>
          <p className="section-description">Select an app to manage its App Store presence</p>
        </div>
        <AppSelector onSelectApp={setSelectedApp} />
      </div>
    );
  }

  return (
  <div className="content-main appstore-manager">
      <div className="card" style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 className="section-title" style={{ marginBottom: '4px' }}>{selectedApp.attributes.name}</h1>
            <p className="section-description">{selectedApp.attributes.bundleId}</p>
          </div>
          <button className="btn btn-secondary" onClick={() => setSelectedApp(null)}>
            ← Back to Apps
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          <span className="alert-icon">⚠️</span>
          <span>{error}</span>
        </div>
      )}

      {loading && (
        <div className="loading-inline">
          <div className="spinner"></div>
        </div>
      )}

      {!loading && (
        <div className="animate-slideUp">
          {/* TestFlight Section */}
          {activeSection === 'testflight' && (
            <div className="card">
              <div className="card-header">
                <h2 className="card-title"><span className="card-title-icon">🚀</span> TestFlight & Builds</h2>
              </div>
              <TestFlightSection 
                appId={selectedApp.id}
                currentVersion={currentVersion}
                onBuildReady={async (updatedVersion) => {
                  if (updatedVersion) {
                    setCurrentVersion(updatedVersion);
                  } else {
                    await loadAppData();
                  }
                }}
              />
            </div>
          )}

          {/* Version Section */}
          {activeSection === 'version' && currentVersion && (
            <div className="card">
              <div className="card-header">
                <h2 className="card-title"><span className="card-title-icon">📦</span> Version Details</h2>
              </div>
              <VersionManager
                app={selectedApp}
                currentVersion={currentVersion}
                onVersionChange={setCurrentVersion}
                onUpdate={loadAppData}
              />
            </div>
          )}

          {/* Screenshots Section */}
          {activeSection === 'screenshots' && currentVersion && (
            <div className="card">
              <div className="card-header">
                <h2 className="card-title"><span className="card-title-icon">📸</span> Screenshots</h2>
              </div>
              <ScreenshotManager
                versionId={currentVersion.id}
                onUpdate={loadAppData}
              />
            </div>
          )}

          {/* Localization Section */}
          {activeSection === 'localization' && currentVersion && (
            <div className="card">
              <div className="card-header">
                <h2 className="card-title"><span className="card-title-icon">🌍</span> Localization</h2>
              </div>
              <LocalizationManager
                versionId={currentVersion.id}
                onUpdate={loadAppData}
              />
            </div>
          )}

          {/* App Information Section */}
          {activeSection === 'information' && (
            <div className="card">
              <div className="card-header">
                <h2 className="card-title"><span className="card-title-icon">ℹ️</span> App Information</h2>
              </div>
              <AppInformation 
                app={selectedApp} 
                appDetails={appDetails}
                currentVersion={currentVersion}
                versions={versions}
                onUpdate={loadAppData}
              />
            </div>
          )}

          {/* Categories Section */}
          {activeSection === 'categories' && (
            <div className="card">
              <div className="card-header">
                <h2 className="card-title"><span className="card-title-icon">📂</span> Categories</h2>
              </div>
              <CategoryManager
                appDetails={appDetails}
                appId={selectedApp.id}
                onUpdate={loadAppData}
              />
            </div>
          )}

          {/* Age Rating Section */}
          {activeSection === 'age-rating' && currentVersion && (
            <div className="card">
              <div className="card-header">
                <h2 className="card-title"><span className="card-title-icon">🎯</span> Age Rating</h2>
              </div>
              <AgeRating
                versionId={currentVersion.id}
                onUpdate={loadAppData}
              />
            </div>
          )}

          {/* Content Rights Section */}
          {activeSection === 'content-rights' && selectedApp && (
            <div className="card">
              <div className="card-header">
                <h2 className="card-title"><span className="card-title-icon">⚖️</span> Content Rights</h2>
              </div>
              <ContentRights
                app={selectedApp}
                appDetails={appDetails}
                onUpdate={loadAppData}
              />
            </div>
          )}

          {/* Review Information Section */}
          {activeSection === 'review' && currentVersion && (
            <div className="card">
              <div className="card-header">
                <h2 className="card-title"><span className="card-title-icon">📝</span> Review Information</h2>
              </div>
              <ReviewInformation
                versionId={currentVersion.id}
                currentVersion={currentVersion}
                onUpdate={loadAppData}
              />
            </div>
          )}

          {/* Pricing Section */}
          {activeSection === 'pricing' && selectedApp && (
            <div className="card">
              <div className="card-header">
                <h2 className="card-title"><span className="card-title-icon">💰</span> Pricing & Availability</h2>
              </div>
              <PricingAvailability 
                appId={selectedApp.id} 
                onUpdate={() => loadAppData()}
              />
            </div>
          )}

          {/* In-App Purchases Section */}
          {activeSection === 'monetization-iap' && selectedApp && (
            <div className="card">
              <div className="card-header">
                <h2 className="card-title"><span className="card-title-icon">🛒</span> In-App Purchases</h2>
              </div>
              <MonetizationIAP appId={selectedApp.id} />
            </div>
          )}

          {/* Subscriptions Section */}
          {activeSection === 'monetization-subscriptions' && selectedApp && (
            <div className="card">
              <div className="card-header">
                <h2 className="card-title"><span className="card-title-icon">💳</span> Subscriptions</h2>
              </div>
              <MonetizationSubscriptions appId={selectedApp.id} />
            </div>
          )}

          {/* Validation Errors */}
          {validationErrors.length > 0 && (
            <div className="validation-errors">
              <h4>⚠️ Please fix the following issues before submitting:</h4>
              <ul>
                {validationErrors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Submit Button */}
          {currentVersion && (
            <div className="card">
              <SubmitButton
                versionId={currentVersion.id}
                onValidate={validateSubmission}
                onSubmitSuccess={loadAppData}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AppStoreManager;
